import React from 'react';
import { useTranslation } from 'react-i18next';
import { Megaphone } from 'lucide-react';
import { Card, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import type { Announcement } from '../../types/dashboard';
import AnnouncementRow from './AnnouncementRow';
import CardScrollBody from './CardScrollBody';
import { DASHBOARD_CARD_HEIGHT, DASHBOARD_CARD_HEADER } from './cardHeight';

interface AnnouncementsCardProps {
  announcements: Announcement[];
  loading?: boolean;
}

const AnnouncementsCard: React.FC<AnnouncementsCardProps> = ({
  announcements,
  loading = false,
}) => {
  const { t } = useTranslation();

  return (
    <Card className={`flex ${DASHBOARD_CARD_HEIGHT} flex-col overflow-hidden`}>
      <CardHeader
        className={`${DASHBOARD_CARD_HEADER} flex-row items-center justify-between space-y-0 border-b py-0`}
      >
        <CardTitle className="flex items-center gap-2 text-sm font-medium">
          <Megaphone className="size-4 text-muted-foreground" strokeWidth={1.9} />
          {t('dashboard.announcements')}
        </CardTitle>
        {!loading && announcements.length > 0 && (
          <Badge variant="secondary" className="revy-figure font-normal">
            {announcements.length}
          </Badge>
        )}
      </CardHeader>

      <CardScrollBody label={t('dashboard.announcements')}>
      {loading ? (
        <div className="divide-y">
          {Array.from({ length: 3 }, (_, index) => (
            <div key={index} className="flex gap-3 p-4">
              <Skeleton className="size-8 shrink-0 rounded-lg" />
              <div className="flex-1 space-y-2">
                <Skeleton className="h-3.5 w-2/3" />
                <Skeleton className="h-3 w-full" />
              </div>
            </div>
          ))}
        </div>
      ) : announcements.length === 0 ? (
        <p className="revy-cjk grid h-full place-items-center p-8 text-center text-sm leading-[1.6] text-muted-foreground">
          {t('dashboard.noAnnouncements')}
        </p>
      ) : (
        <ul className="divide-y">
          {announcements.map((announcement) => (
            <AnnouncementRow key={announcement.id} announcement={announcement} />
          ))}
        </ul>
      )}
      </CardScrollBody>
    </Card>
  );
};

export default AnnouncementsCard;
